// regelfil: src/AGENTS.md
import { StickyActionBar } from "@/components/site/StickyActionBar";
import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { absoluteUrl, OG_DEFAULT_IMAGE } from "@/lib/og";
import { RouteErrorState } from "@/components/site/RouteErrorState";
import { SchoolProfileSkeleton } from "@/components/site/SchoolProfileSkeleton";
import { useSuspenseQuery, queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { trackGa4 } from "@/lib/analytics";
import { ShareButtons } from "@/components/site/ShareButtons";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import {
  sourceEyebrow,
  sourceRegisterLabel,
  sourceShortLabel,
  unitNoun,
  unitNounLower,
} from "@/lib/unit-source";

import {
  BadgeCheck,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building2,
  GraduationCap,
  ShieldCheck,
  ArrowRight,
  MessageSquare,
  Star,
  PenSquare,
} from "lucide-react";
import { getPublicSchool } from "@/lib/public-school.functions";
import { coverageStatsQueryOptions } from "@/lib/coverage-stats";
import { listUpcomingOpenHouseEvents } from "@/lib/open-house.functions";
import { listPublicVacancies } from "@/lib/vacancies.functions";
import { getMunicipalityChoice } from "@/lib/municipality-choice.functions";
import { listExternalJobsForSchool } from "@/lib/external-jobs.functions";
import { getEducationUnitBySlug } from "@/lib/education-units.functions";
import {
  dormantNotice,
  isGoneUnitStatus,
  isVisibleUnitStatus,
} from "@/lib/education-unit-status";

import { orMissing, type EducationUnit } from "@/lib/education-units";
import { listApprovedReviews, getReviewStats } from "@/lib/reviews.functions";
import { ClaimStatusBanner } from "@/components/site/ClaimStatusBanner";
import { useSchoolOverride } from "@/lib/school-overrides";
import { WhyThisSchool } from "@/components/site/WhyThisSchool";
import { ReviewCard } from "@/components/site/ReviewCard";
import { StarRating } from "@/components/site/StarRating";
import { safeBuildSchoolMock } from "@/lib/school-mock-schema";
import { MockDataError } from "@/components/site/MockDataError";
import { Breadcrumbs, buildSchoolCrumbs, crumbsToJsonLd } from "@/components/site/Breadcrumbs";
import { skolansJamforelse } from "@/lib/jamforstapel";
import { heroSourcesFor } from "@/lib/hero-images";
import { ogImageFor, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from "@/lib/og-images";
import {
  ProfileHero,
  StickyNav,
  Gallery,
} from "@/components/site/SchoolProfileSections";
import { ReportContentDialog } from "@/components/ReportContentDialog";
import { TrygghetStudieroQA } from "@/components/site/TrygghetStudieroQA";
import {
  PrincipalWelcome,
  MeetTheTeam,
  ADayHere,
} from "@/components/site/SchoolProfileHumans";
import { SourceBadge, StaleWarning, VerifiedBadge } from "@/components/site/SourceBadge";
import { buildMergedSchoolFaq } from "@/lib/school-faq";
import { formatFullAddress, formatGradeYears, formatHuvudman, formatPhone, huvudmanOrMissing, telHref } from "@/lib/school-format";
import {
  SchoolQuickFacts,
  SchoolOfferings,
  SchoolFaq,
  SchoolInterestForm,
} from "@/components/site/SchoolProfileBlocks";
import { NearbySchools } from "@/components/site/SchoolProfileDecision";
import { SchoolSocialBlock } from "@/components/site/SchoolSocialBlock";
import { SchoolStatistics } from "@/components/site/SchoolStatistics";
import { SchoolKeyFigures } from "@/components/site/SchoolKeyFigures";
import { getSchoolFigures } from "@/lib/school-figures.functions";
import { OpenHouseSection } from "@/components/site/OpenHouseSection";
import { SchoolPlatsbankenJobs } from "@/components/site/SchoolPlatsbankenJobs";
import { VacanciesSection } from "@/components/site/VacanciesSection";
import { buildKeyFigures } from "@/lib/school-key-figures";
import { kommunAdress } from "@/lib/kommun-adress";
import { MunicipalityChoice } from "@/components/site/MunicipalityChoice";
import type { ChoiceInfo } from "@/lib/municipality-choice";
import { buildSchoolNarrative, latestPupils, primarySkolform, schoolDescription, schoolTitle } from "@/lib/school-narrative";
import { comparePeriodDesc } from "@/lib/school-statistics";
import { listPublishedSchoolPhotos } from "@/lib/school-media.functions";
import { hasValue } from "@/lib/empty-values";
import { skolansKartlank } from "@/lib/skolans-lage";




/** Basala slug-krav — vi vill inte slå mot databasen med tomt/ogiltigt värde. */
export const SLUG_PATTERN = /^[a-z0-9-]{1,200}$/i;

export const detailOpts = (slug: string) =>
  queryOptions({
    queryKey: ["education_units", "detail", slug] as const,
    // React Query skickar in en AbortSignal som avbryts när frågan blir stale
    // (t.ex. vid routebyte). Vi forwardar den till server-funktionen så att
    // pågående nätverksanrop kan cancellas i stället för att slutföras i onödan.
    queryFn: ({ signal }) => getEducationUnitBySlug({ data: { slug }, signal }),
    staleTime: 5 * 60_000,
    // Retry bara på 5xx/nätverksfel, inte på 404/notFound.
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : "";
      if (/not\s*found|404|invalid/i.test(msg)) return false;
      return failureCount < 2;
    },
  });

export const photosOpts = (schoolUnitCode: string) =>
  queryOptions({
    queryKey: ["school_media", "published", schoolUnitCode] as const,
    queryFn: () => listPublishedSchoolPhotos({ data: { schoolUnitCode } }),
    staleTime: 5 * 60_000,
  });

export const figuresOpts = (schoolUnitCode: string, municipalityCode: string | null) =>
  queryOptions({
    queryKey: ["school_figures", schoolUnitCode, municipalityCode] as const,
    queryFn: () => getSchoolFigures({ data: { schoolUnitCode, municipalityCode } }),
    staleTime: 30 * 60_000,
  });

export const Route = createFileRoute("/skola/$slug")({
  // Validera route-parametrarna innan loadern körs — saknad eller ogiltig
  // slug ska aldrig resultera i en fetch.
  parseParams: (raw) => {
    const slug = (raw.slug ?? "").trim();
    if (!SLUG_PATTERN.test(slug)) throw notFound();
    return { slug };
  },
  loader: async ({ params, context, abortController }) => {
    const bundle = await context.queryClient.ensureQueryData(queryOptions({
      queryKey: ["public-school", params.slug],
      queryFn: () => getPublicSchool({ data: { slug: params.slug }, signal: abortController.signal }),
      staleTime: 60_000,
    }));
    if (!bundle) throw notFound();
    const { unit, photos, figures, verification, nearby, ownProfile, placement } = bundle;
    const gone = isGoneUnitStatus(unit.status);
    const form = unit.education_types?.[0] ?? null;
    context.queryClient.setQueryData(detailOpts(params.slug).queryKey, unit);
    context.queryClient.setQueryData(photosOpts(unit.school_unit_code).queryKey, photos);
    context.queryClient.setQueryData(figuresOpts(unit.school_unit_code, unit.municipality_code ?? null).queryKey, figures);
    context.queryClient.setQueryData(["eu-nearby", unit.slug, form, unit.latitude, unit.longitude], nearby ?? { mode: "alfabetisk", items: [], utanLage: 0 });
    context.queryClient.setQueryData(["reviews", "school", params.slug], bundle.reviews);
    context.queryClient.setQueryData(["reviews", "stats", params.slug], bundle.reviewStats);
    const [openHouse, vacancies, choice, platsbanken, coverage] = await Promise.all([
      listUpcomingOpenHouseEvents({ data: { schoolSlug: params.slug, schoolUnitCode: unit.school_unit_code } }).catch(() => []),
      listPublicVacancies({ data: { schoolSlug: params.slug, schoolUnitCode: unit.school_unit_code } }).catch(() => []),
      unit.municipality_code ? getMunicipalityChoice({ data: { code: unit.municipality_code } }).catch(() => []) : Promise.resolve([]),
      listExternalJobsForSchool({ data: { schoolUnitCode: unit.school_unit_code, limit: 6 } }).then(r => r.direct).catch(() => []),
      context.queryClient.ensureQueryData(coverageStatsQueryOptions()),
    ]);
    context.queryClient.setQueryData(coverageStatsQueryOptions().queryKey, coverage);
    const first = (nearby as { mode?: string; items?: Array<{ name: string; distance_km?: number }> } | undefined);
    const nearest =
      first?.mode === "geo" && first.items?.[0] && typeof first.items[0].distance_km === "number"
        ? { name: first.items[0].name, distanceKm: first.items[0].distance_km }
        : null;

    return {
      unit,
      gone,
      photos,
      choice,
      figures,
      verified: Boolean(verification.verified),
      ownProfile,
      openHouse,
      vacancies,
      placement,
      nearest,
      platsbanken,
    };


  },
  pendingMs: 200,
  pendingMinMs: 400,
  pendingComponent: SchoolProfileSkeleton,
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Skolan hittades inte — Skolvyn" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    if (loaderData.gone) {
      // Sidan ligger kvar med allt skolan fyllt i, men utan indexering: en
      // enhet kan saknas i registret för att skolan bytt huvudman och fått
      // en ny skolenhetskod.
      const namn = loaderData.unit?.name ?? "Skolenheten";
      return {
        meta: [
          { title: `${namn} — finns inte i Skolverkets register just nu · Skolvyn` },
          {
            name: "description",
            content: `${namn} finns inte i Skolverkets skolenhetsregister just nu. Sidan ligger kvar med skolans egna uppgifter.`,
          },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }

    const s = loaderData.unit;
    // Innehållsgolvet, samma regel som sitemapen (indexable_schools()): namn,
    // adress och skolform. Statistik krävs inte — en förälder som söker på en
    // liten eller ny skola ska hitta den (beslut 8 oktober 2026).
    const harAdress = Boolean((s.visit_address || s.postal_address || "").trim());
    // Förskolan har sin egen källa: enheterna kommer från Kolada, adressen
    // från SCB — inte från Skolverkets skolenhetsregister.
    const arForskola = s.education_types.some((t) => /^förskola$/i.test(t));
    const indexerbar =
      Boolean(s.name?.trim()) && harAdress && s.education_types.length > 0;
    const location = [s.municipality_name, s.county_name].filter(Boolean).join(", ");
    const kalla = arForskola ? "Kolada (RKA) och SCB" : "Skolverket";
    const narrative = buildSchoolNarrative({
      unit: s,
      rows: loaderData.figures?.rows ?? [],
      placement: loaderData.placement ?? null,
      pupils: latestPupils(loaderData.figures?.rows ?? []),
      nearest: loaderData.nearest ?? null,
      benchmarks: loaderData.figures?.benchmarks ?? [],
    });
    const summary =
      schoolDescription([
        ...narrative.faktaruta,
        // Skolans egna tal: första meningen i varje placering (själva talet).
        ...narrative.placeringar.map((p) => p.text.split(/(?<=\.)\s+/)[0]),
      ]) ||
      `${s.name} i ${orMissing(s.municipality_name)}. ${s.education_types.join(", ") || "Skolenhet"}. Huvudman: ${huvudmanOrMissing(s.organizer_name)}. Källa: ${kalla}.`;

    // Preload av LCP-hero: eftersom mock:en är deterministisk från
    // school_unit_code kan vi räkna ut vilken bild ProfileHero kommer att rendera
    // och köa den redan innan React monterat. AVIF föredras — webbläsare som
    // saknar stöd ignorerar `type=image/avif` och hämtar via <picture> istället.
    const mockResult = safeBuildSchoolMock(s);
    const ownHero = loaderData.photos?.[0] ?? null;
    const preloadLinks = ownHero
      ? [
          {
            rel: "preload" as const,
            as: "image" as const,
            type: "image/webp",
            imageSrcSet: ownHero.srcSet,
            imageSizes: "100vw",
            fetchPriority: "high" as const,
          },
        ]
      : // Utan eget foto renderas ingen bild i heron — då finns inget att
        // förladda heller.
        [];


    const canonicalUrl = `https://skolvyn.se/skola/${params.slug}`;
    const ogTitle = `${s.name}${location ? ` — ${location}` : ""} · Skolvyn`;
    const ogDescription = summary.slice(0, 160);
    // Delningsbilden: skolans egen publicerade huvudbild om den finns
    // (publik, cachebar 1200×750-variant), annars den byggda genrebilden.
    // Aldrig en signerad lagrings-URL — den slutar fungera efter en timme.
    const heroPhoto = loaderData.photos?.[0] ?? null;
    const ogImage = heroPhoto
      ? absoluteUrl(heroPhoto.ogPath)
      : mockResult.ok
        ? absoluteUrl(ogImageFor(mockResult.mock.hero.image))
        : OG_DEFAULT_IMAGE;

    const heroCaption = heroPhoto
      ? heroPhoto.altText.trim()
      : mockResult.ok
        ? mockResult.mock.hero.caption.trim()
        : "";
    const ogImageAlt = heroCaption
      ? heroCaption.toLocaleLowerCase("sv").includes(s.name.toLocaleLowerCase("sv"))
        ? heroCaption
        : `${s.name} — ${heroCaption}`
      : `${s.name} — skolprofil`;

    return {
      meta: [
        { title: schoolTitle(s) },
        { name: "description", content: ogDescription },
        // Bara enheter som får synas i listor får indexeras. Vilande,
        // planerade och opublicerade enheter (inklusive vår provenhet) ska
        // aldrig konkurrera med aktiva skolor i sökresultaten.
        ...(!isVisibleUnitStatus(s.status) || !indexerbar
          ? [{ name: "robots", content: "noindex, follow" }]
          : []),

        // Open Graph — konsekvent med startsidan och övriga leaf-routes
        { property: "og:title", content: ogTitle },
        { property: "og:description", content: ogDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:site_name", content: "Skolvyn" },
        { property: "og:locale", content: "sv_SE" },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { property: "og:image:secure_url", content: ogImage },
              { property: "og:image:alt", content: ogImageAlt },
              { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
              { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
            ]
          : []),
        // Twitter Card — samma titel, beskrivning och bild som OG
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ogTitle },
        { name: "twitter:description", content: ogDescription },
        ...(ogImage
          ? [
              { name: "twitter:image", content: ogImage },
              { name: "twitter:image:alt", content: ogImageAlt },
            ]
          : []),
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        ...preloadLinks,
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: s.name,
            identifier: s.school_unit_code,
            description: summary,
            url: s.website || undefined,
            telephone: s.phone || undefined,
            email: s.email || undefined,
            image: mockResult.ok ? mockResult.mock.hero.image : undefined,
            logo: mockResult.ok ? mockResult.mock.hero.image : undefined,
            address: {
              "@type": "PostalAddress",
              streetAddress: s.visit_address || s.postal_address || undefined,
              postalCode: s.visit_postal_code || s.postal_code || undefined,
              addressLocality: s.visit_city || s.postal_city || undefined,
              addressRegion: s.county_name || undefined,
              addressCountry: "SE",
            },
            parentOrganization: s.organizer_name
              ? { "@type": "Organization", name: formatHuvudman(s.organizer_name) ?? s.organizer_name, identifier: s.organizer_organization_number || undefined }
              : undefined,
            // Officiell webbplats markeras både som `url` (kanonisk) och via
            // `mainEntityOfPage` för att Google ska koppla profilen till
            // skolans egen sajt utan att förväxla den med Skolvyn-URL:en.
            mainEntityOfPage: s.website || undefined,
            additionalProperty: s.website
              ? [{ "@type": "PropertyValue", propertyID: "officialWebsite", value: s.website }]
              : undefined,
            // sameAs länkar de officiella profilerna i sociala medier till
            // skolans entitet — hjälper Google Knowledge Panel att koppla
            // ihop skolan med sina kanaler. Officiell webbplats ingår också.
            sameAs: (() => {
              const urls: string[] = [];
              if (s.website) urls.push(s.website);
              return urls.length > 0 ? Array.from(new Set(urls)) : undefined;
            })(),
          }),

        },
        {
          type: "application/ld+json",
          children: JSON.stringify(crumbsToJsonLd(buildSchoolCrumbs(s))),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: buildMergedSchoolFaq(s).map((qa) => ({
              "@type": "Question",
              name: qa.q,
              acceptedAnswer: { "@type": "Answer", text: qa.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-section text-center">
      <h1 className="font-display text-h1">Skolenheten hittades inte</h1>
      <p className="mt-2 text-ink-muted">Länken kan vara fel, eller så har enheten upphört enligt Skolverkets register.</p>
      <Link to="/skolor" className="mt-6 inline-flex rounded-card bg-primary px-4 py-2.5 text-small font-semibold text-primary-foreground hover:bg-primary-hover">
        Sök i registret
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <RouteErrorState
      title="Kunde inte hämta skolenheten"
      description="Vi når inte Skolverkets skolenhetsregister just nu. Det brukar vara tillfälligt — försök igen om en stund, eller sök upp skolan på nytt via registret."
      error={error}
      fallbackActions={[
        { to: "/skolor", label: "Sök i registret", variant: "primary" },
        { to: "/", label: "Till startsidan", variant: "ghost" },
      ]}
    />
  ),
  component: SkolProfil,
});

function SkolProfil() {
  const { unit, gone, photos, figures, verified, ownProfile, openHouse, vacancies, placement, nearest, choice, platsbanken } = Route.useLoaderData();
  const keyFigures = useMemo(
    () => buildKeyFigures(figures?.rows ?? [], figures?.benchmarks ?? [], { alla: true }),
    [figures],
  );
  const pupils = useMemo(() => {
    // Elevantalet rapporteras per skolform och läsår. Summera bara det
    // senaste läsåret per skolform — annars räknas samma elever flera gånger
    // så fort ett nytt år kommer in.
    const latestByForm = new Map<string, number>();
    const periodByForm = new Map<string, string>();
    for (const r of figures?.rows ?? []) {
      if (r.metric !== "totalNumberOfPupils" || typeof r.value_num !== "number") continue;
      const prev = periodByForm.get(r.skolform);
      if (prev === undefined || comparePeriodDesc(r.time_period, prev) < 0) {
        periodByForm.set(r.skolform, r.time_period);
        latestByForm.set(r.skolform, Number(r.value_num));
      }
    }
    if (latestByForm.size === 0) return null;
    let sum = 0;
    for (const v of latestByForm.values()) sum += v;
    return sum;
  }, [figures]);
  const heroPhoto = photos?.[0] ?? null;
  const galleryPhotos = (photos ?? []).slice(1);
  const s = unit as EducationUnit;
  const kartlank = skolansKartlank(s);
  const mockResult = useMemo(() => safeBuildSchoolMock(s), [s]);
  const override = useSchoolOverride(s.slug);
  // Jsonb-fälten är [] eller {} tills skolan lagt in något.
  const hasStaff = Array.isArray(ownProfile?.staff) && ownProfile.staff.length > 0;
  const ownDescription =
    (hasValue(ownProfile?.description) ? (ownProfile?.description as string) : null) ??
    (override?.description || null);
  const hasDaySchedule =
    Array.isArray(ownProfile?.day_schedule) && ownProfile.day_schedule.length > 0;
  const narrative = useMemo(
    () =>
      buildSchoolNarrative({
        unit: unit as EducationUnit,
        rows: figures?.rows ?? [],
        placement: placement ?? null,
        pupils: latestPupils(figures?.rows ?? []),
        nearest: nearest ?? null,
        benchmarks: figures?.benchmarks ?? [],
      }),
    [unit, figures, placement, nearest],
  );
  // Registrets uppgift skrivs aldrig över — skolans egen uppgift ligger vid
  // sidan av och visas med källrad. Registeradressen är vår enda kanal till de
  // skolor som ännu inte tagit över sin sida, och dit går vidarebefordran.
  const schoolPhone = hasValue(ownProfile?.contact_phone) ? (ownProfile?.contact_phone as string) : null;
  const schoolEmail = hasValue(ownProfile?.contact_email) ? (ownProfile?.contact_email as string) : null;
  const schoolWebsite = hasValue(ownProfile?.contact_website) ? (ownProfile?.contact_website as string) : null;
  const contactPhone = schoolPhone ?? override?.phone ?? s.phone;
  const contactEmail = schoolEmail ?? override?.email ?? s.email;
  const contactWebsite = schoolWebsite ?? override?.website ?? s.website;
  // Kontaktuppgifterna kan vara berikade från en annan källa än enhetens
  // register (t.ex. Stockholms stads Hitta service). Källan namnges där
  // uppgiften visas — aldrig bara enhetens registerkälla.
  const contactSourceName = s.contact_source ?? s.source_name;
  const contactSourceUrl = s.contact_source_url ?? s.source_url;
  const fetchedDate = new Date(s.fetched_at).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });


  // GA4 `view_school_profile` — logga profilvisning en gång per slug.
  useEffect(() => {
    trackGa4("view_school_profile", {
      item_id: s.slug,
      item_name: s.name,
      school_unit_code: s.school_unit_code,
      municipality: s.municipality_name ?? undefined,
      county: s.county_name ?? undefined,
      organizer: s.organizer_name ?? undefined,
      education_type: s.education_types[0] ?? undefined,
      verified: verified,
    });
  }, [s.slug, s.name, s.school_unit_code, s.municipality_name, s.county_name, s.organizer_name, s.education_types, verified]);

  // D: anonym visning till skolans statistik (inget om besökaren sparas).
  useEffect(() => {
    void import("@/lib/profile-views.functions").then((m) => m.sendProfileView(s.school_unit_code));
  }, [s.school_unit_code]);


  // En enhet som saknas i registret får INTE en tom sida: skolan kan ha bytt
  // huvudman och fått ny skolenhetskod, och rektorn kan ha fyllt sidan med
  // eget innehåll. Sidan ligger kvar med en tydlig notis, utan indexering och
  // utan plats i sidkartan.
  const notice = gone
    ? "Den här skolenheten finns inte i Skolverkets register just nu. Det kan bero på att skolan bytt huvudman och fått en ny skolenhetskod. Uppgifterna nedan är de senast hämtade."
    : dormantNotice(s.status, s.fetched_at);

  // Böjning efter skolform. "Skolan har inte lagt till egna uppgifter" på en
  // förskolesida är det första en förskolechef reagerar på.
  const noun = unitNoun(s.education_types);
  const nounLower = unitNounLower(s.education_types);
  const nounIndef = unitNounLower(s.education_types, "indefinite");
  // Antagningsvägen är det enda som skiljer sig mellan skolor — resten av
  // regelverket ligger i guiden.
  const isFristaende = /enskild|fristående/i.test(s.organizer_type ?? "");
  // "Erbjudanden" upprepar Utbildning-kortet om det inte finns något utöver
  // skolform och årskurs. Då visas det inte alls. Värden som BETYDER frånvaro
  // ("Ej relevant", bindestreck, tom sträng) räknas inte som innehåll —
  // listan ligger i src/lib/empty-values.ts och gäller överallt.
  const hasOfferings =
    hasValue(s.gymnasium_programs) ||
    hasValue(s.komvux_levels) ||
    hasValue(s.orientation) ||
    hasValue(s.resource_school) ||
    hasValue(s.principal_name);


  if (!mockResult.ok) {
    return (
      <div className="bg-background">
        <MockDataError
          unitName={s.name}
          stage={mockResult.stage}
          issues={mockResult.issues}
          thrown={mockResult.thrown}
        />
      </div>
    );
  }
  const mock = mockResult.mock;
  // Flikraden i serverns svar: bara avsnitt som säkert renderas. I webbläsaren
  // rättas listan mot sidan. Flikarna är ankarlänkar, inget innehåll döljs.
  const navIds = [
    "statistik",
    "foralder-faq",
    ...((platsbanken ?? []).length > 0 ? ["platsbanken-jobb"] : []),
    "omdomen",
    "fakta",
    "intresseanmalan",
    ...(openHouse.length > 0 ? ["oppet-hus"] : []),
    ...(hasOfferings ? ["erbjudanden"] : []),
  ];



  return (
    <div className="bg-background">
      {notice && (
        <div className="border-b border-amber-200 bg-amber-50">
          <div className="mx-auto max-w-6xl px-5 py-3 text-small text-amber-900 lg:px-8">
            <strong className="font-semibold">Observera:</strong> {notice}
          </div>
        </div>
      )}
      {/* Profilstatus visas bara för verifierade profiler; källan står vid faktarutorna. */}
      {verified ? (
<div className={`border-b border-border ${verified ? "bg-emerald-50" : "bg-primary-soft/40"}`}>
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-2.5 text-small lg:px-8">
          <p className="flex items-center gap-2 text-ink">
            <ShieldCheck className={`h-4 w-4 ${verified ? "text-emerald-700" : "text-primary"}`} aria-hidden />
            {verified ? (
              <span>
                <strong className="text-emerald-800">Verifierad profil</strong> — {unitNounLower(s.education_types)} har gjort anspråk och en registrerad
                företrädare förvaltar innehållet. Officiella kanaler är bekräftade.
              </span>
            ) : (
              /* Raden förklarar för föräldern. Inbjudan till skolan ligger i
                 knappen strax under — samma uppmaning två gånger hjälper ingen. */
              <span>
                Uppgifterna kommer från{" "}
                {sourceRegisterLabel(s.education_types)}.{" "}
                <Link to="/om/metod" className="font-semibold underline underline-offset-2 hover:text-primary">
                  Så bygger vi profilerna
                </Link>
                .
              </span>
            )}
          </p>
        </div>
      </div>
      ) : null}


      <ProfileHero
        unit={s}
        mock={mock}
        photo={heroPhoto}
        verified={verified}
        jamforelse={heroPhoto ? null : skolansJamforelse(figures?.rows ?? [], figures?.benchmarks ?? [], s.municipality_code ?? null)}
      />
      <StickyNav ids={navIds} />

      <div className="mx-auto max-w-6xl px-5 pt-6 lg:px-8">
        <Breadcrumbs items={buildSchoolCrumbs(s)} />

        {/* A2 + B16: vad det här är för skola, härlett ur uppgifterna, med källa. */}
        <section aria-label={`Fakta om ${s.name}`} className="mt-4 max-w-3xl space-y-2 text-body text-ink">
          {narrative.faktaruta.map((m) => (
            <p key={m}>{m}</p>
          ))}
          {narrative.faktaKallor.length > 0 && (
            <p className="text-small text-ink-muted">Källor: {narrative.faktaKallor.join(" · ")}</p>
          )}
        </section>

        {/* Inbjudan till skolan står efter det föräldern kom för, inte före. */}
        {!verified && (
          <div className="mt-4">
          <Link
            to="/skola/$slug/ta-over"
            params={{ slug: s.slug }}
            onClick={() => trackGa4("school_cta_click", { cta: "ta-over-hero", item_id: s.slug })}
            className="inline-flex items-center gap-2 rounded-card border border-border bg-surface px-4 py-2 text-small font-semibold text-ink transition hover:border-primary hover:text-primary"
          >
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
            Är detta din {nounIndef}? Ta över sidan — gratis
          </Link>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <FavoriteButton
            variant="chip"
            school={{
              slug: s.slug,
              name: s.name,
              municipality: s.municipality_name,
              county: s.county_name,
              educationType: s.education_types[0] ?? null,
              // Ingen genrebild sparas — bara skolans egna foton får visas.
              image: heroPhoto?.src ?? null,
            }}
          />
          <Link to="/sparat" className="text-small font-medium text-ink-muted underline underline-offset-2 hover:text-primary">
            Se dina sparade skolor
          </Link>
        </div>

        <ClaimStatusBanner schoolUnitCode={s.school_unit_code} schoolSlug={s.slug} />

        <SchoolQuickFacts unit={s} />
      </div>

      <div className="mx-auto max-w-6xl px-5 pb-28 lg:px-8 lg:pb-16">
        {/* A3: siffrorna — sidans skäl att finnas. Placering och utveckling
            vävs in i måttets eget kort; inget mått står två gånger. */}
        <div className="mt-10">
          <SectionHeader
            id="statistik"
            eyebrow="Officiell statistik"
            title={`Siffror om ${nounLower}`}
          />
          {keyFigures.length > 0 ? (
            <SchoolKeyFigures
              figures={keyFigures}
              municipalityName={s.municipality_name}
              placering={Object.fromEntries(narrative.placeringar.map((p) => [`${p.skolform}|${p.metric}`, { text: p.text, kalla: p.kalla }]))}
              utveckling={Object.fromEntries(narrative.utveckling.map((u) => [`${primarySkolform(s)}|${u.metric}`, { text: u.text, kalla: u.kalla }]))}
            />
          ) : (
            <SchoolStatistics rows={figures?.rows ?? []} />
          )}
        </div>

        {/* A4: så söker man plats i kommunen. */}
        {(() => {
          const t = (s.education_types[0] ?? "").toLocaleLowerCase("sv");
          const kind = t === "förskola" ? "forskola" : t === "grundskola" || t === "förskoleklass" ? "grundskola" : null;
          return kind ? <MunicipalityChoice items={(choice ?? []) as ChoiceInfo[]} only={kind} /> : null;
        })()}

        {/* A5: närmaste alternativ. */}
        <NearbySchools unit={s} />

        {/* Den personliga guiden står efter förälderns läsning, inte mitt i den. */}
        <WhyThisSchool unit={s} />

        {/* A6: vanliga frågor om just den här skolan. */}
        <SchoolFaq id="foralder-faq" items={buildMergedSchoolFaq(s)} schoolName={s.name} />

        {/* A7: lediga jobb med källa och länk till originalannonsen. */}
        <div className="mt-10">
          <SchoolPlatsbankenJobs jobs={platsbanken ?? []} schoolName={unit.name} />
        </div>

        {/* A8: omdömen. */}
        <SchoolReviews slug={s.slug} schoolName={s.name} />

        {/* A9: kontakt och intresseanmälan. */}
        {/* Officiella uppgifter */}
        <SectionHeader
          id="fakta"
          eyebrow={sourceEyebrow(s.education_types)}
          title="Officiella uppgifter"
          description={`Källa: ${s.source_name}${s.fetched_at ? `, hämtad ${formatSwedishDate(s.fetched_at)}` : ""}.`}
        />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <Card
            icon={<Building2 className="h-4 w-4" />}
            title="Huvudman — den som driver verksamheten"
          >
            <Row label="Namn" value={formatHuvudman(s.organizer_name)} verified verifiedSource={s.source_name} />
            <Row label="Organisationsnummer" value={s.organizer_organization_number} mono verified verifiedSource={s.source_name} />
            <Row label="Typ" value={s.organizer_type} verified verifiedSource={s.source_name} />
            <Row label="Juridisk form" value={s.legal_form} verified verifiedSource={s.source_name} />
          </Card>

          <Card
            icon={<MapPin className="h-4 w-4" />}
            title="Adress"
          >
            <Row label="Besöksadress" value={formatFullAddress(s.visit_address, s.visit_postal_code, s.visit_city)} />
            <Row label="Postadress" value={formatFullAddress(s.postal_address, s.postal_code, s.postal_city)} />
            {kartlank && <div className="pb-3"><dt className="sr-only">Karta</dt><dd><a href={kartlank} target="_blank" rel="noopener" className="text-small font-semibold text-primary underline underline-offset-2">Visa på karta</a></dd></div>}
            <Row label="Kommun" value={s.municipality_name} />
            <Row label="Län" value={s.county_name} />
          </Card>

          {(contactPhone || contactEmail || contactWebsite || s.principal_name) ? (
          <Card
            icon={<Phone className="h-4 w-4" />}
            title="Kontakt"
            source={
              override && (override.phone || override.email || override.website) ? (
                <SourceBadge kind="skolan" updatedAt={override.publishedAt ?? override.submittedAt} />
              ) : (
                <SourceBadge kind="offentlig" source={contactSourceName} />
              )
            }
            updatedAt={
              override && (override.phone || override.email || override.website)
                ? override.publishedAt ?? override.submittedAt ?? s.fetched_at
                : s.fetched_at
            }
            sourceName={
              override && (override.phone || override.email || override.website)
                ? "Skolan själv"
                : contactSourceName
            }
            sourceUrl={
              override && (override.phone || override.email || override.website)
                ? null
                : contactSourceUrl
            }
          >
            <Row label="Telefon" value={contactPhone ? <a className="text-primary underline underline-offset-2" href={telHref(contactPhone) ?? undefined} onClick={() => trackGa4("school_contact_click", { method: "phone", item_id: s.slug })}>{formatPhone(contactPhone)}</a> : null} note={<ContactSource fromSchool={Boolean(schoolPhone)} sourceName={contactSourceName} />} />
            <Row label="E-post" value={contactEmail ? <a className="text-primary underline underline-offset-2" href={`mailto:${contactEmail}`} onClick={() => trackGa4("school_contact_click", { method: "email", item_id: s.slug })}>{contactEmail}</a> : null} icon={<Mail className="h-3.5 w-3.5" />} note={<ContactSource fromSchool={Boolean(schoolEmail)} sourceName={contactSourceName} />} />
            <Row label="Webbplats" value={contactWebsite ? <a className="text-primary underline underline-offset-2" href={ensureUrl(contactWebsite)} target="_blank" rel="noreferrer noopener" onClick={() => trackGa4("school_contact_click", { method: "website", item_id: s.slug })}>{contactWebsite}</a> : null} icon={<Globe className="h-3.5 w-3.5" />} note={<ContactSource fromSchool={Boolean(schoolWebsite)} sourceName={contactSourceName} />} />
            <Row label="Rektor" value={s.principal_name} verified verifiedSource={s.source_name} />
            {override && (override.phone || override.email || override.website) && (
              <p className="mt-2 text-small text-emerald-700">✓ Uppdaterad av {nounLower}</p>
            )}
          </Card>
          ) : null}

          <Card
            icon={<GraduationCap className="h-4 w-4" />}
            title="Utbildning"
          >
            <Row label="Verksamhet" value={s.education_types.join(", ") || null} verified verifiedSource={s.source_name} />
            {s.grade_years.length > 0 && <Row label="Årskurser" value={formatGradeYears(s.grade_years)} verified verifiedSource={s.source_name} />}
            {s.gymnasium_programs.length > 0 && (
              <div className="border-t border-border pt-3">
                <p className="eyebrow text-small">Gymnasieprogram</p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {s.gymnasium_programs.map((p) => (
                    <li key={p} className="rounded-pill bg-background px-2.5 py-1 text-small">{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {s.komvux_levels.length > 0 && (
              <div className="border-t border-border pt-3">
                <p className="eyebrow text-small">Komvux-nivåer</p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {s.komvux_levels.map((p) => (
                    <li key={p} className="rounded-pill bg-background px-2.5 py-1 text-small">{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {hasValue(s.orientation) && <Row label="Inriktning" value={s.orientation as string} />}
            {s.resource_school && <Row label="Resursskola" value="Ja" />}
          </Card>
        </div>

        <SchoolInterestForm unit={s} />

        {/* A10: platser som en övertagen skola fyller — annars visas de inte alls. */}
        {ownDescription && (
          <section className="mt-6 rounded-card border border-emerald-200 bg-emerald-50/60 p-card">
            <p className="eyebrow text-emerald-800">Från {nounLower} · verifierad</p>
            <p className="mt-2 whitespace-pre-line text-small text-ink">{ownDescription}</p>
          </section>
        )}
        {openHouse.length > 0 && <OpenHouseSection slug={s.slug} events={openHouse} />}
        {vacancies.length > 0 && <VacanciesSection vacancies={vacancies} />}
        {verified && (
          <>
        {/* Sektionerna nedan är skolans egna. En verifierad sida utan innehåll
            ska visa ingenting alls hellre än en tom mall. */}
        {hasValue(ownProfile?.principal_greeting) && <PrincipalWelcome unit={s} />}

        {hasStaff && <MeetTheTeam unit={s} />}

        {hasDaySchedule && <ADayHere unit={s} />}

        {override?.trygghetQA && (
          <section id="trygghet" className="scroll-mt-20 pt-10">
            <TrygghetStudieroQA
              qa={override.trygghetQA}
              elevrostor={undefined}
              schoolName={s.name}
            />
          </section>
        )}
          </>
        )}

        {(verified || galleryPhotos.length > 0) && (
          <>
            <Gallery mock={mock} photos={galleryPhotos} />
            {galleryPhotos.length > 0 ? (
              <p className="mt-2">
                <ReportContentDialog
                  schoolUnitCode={s.school_unit_code}
                  contentType="media"
                  contentId={String(galleryPhotos[0]!.id)}
                  label="Anmäl en bild"
                  what={`Bilder som ${s.name} själva publicerat`}
                />
              </p>
            ) : null}
          </>
        )}

        {hasOfferings && <SchoolOfferings unit={s} />}

        {/* Det som gäller varje skola i Sverige ligger i guiden, inte här. */}
        <section className="mt-10 rounded-card border border-border bg-surface p-card">
          <p className="text-small font-semibold text-ink">
            Antagning, kö, stöd och frågor att ställa vid besöket
          </p>
          {/* Ingen mening om hur just den här skolan söks: den gäller en riktig
              skolas process och får bara stå med källa (kommunens egen sida). */}
          <Link
            to="/guider/skolvalet"
            className="mt-3 inline-flex items-center gap-1.5 text-small font-semibold text-primary underline underline-offset-2"
          >
            Läs guiden: skolvalet steg för steg <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        {verified && <SchoolSocialBlock unit={s} />}

        {/* B15: interna länkar till kommunen och länet. */}
        {s.municipality_slug && s.municipality_name && (
          <p className="mt-10 text-small">
            <Link
              to="/skolor/$kommun"
              params={{ kommun: kommunAdress(s.municipality_slug, s.county_slug) ?? s.municipality_slug }}
              className="font-semibold text-primary underline underline-offset-2"
            >
              Alla {narrative.formPlural} i {s.municipality_name}
            </Link>
          </p>
        )}

        <section className="mt-10 rounded-card border border-border bg-surface p-card text-small">
          <p className="eyebrow">Om uppgifterna</p>
          <p className="mt-2 text-ink-muted">
            Skolenhetskod <span className="mono text-ink">{s.school_unit_code}</span>. Hämtat från {sourceRegisterLabel(s.education_types)} {fetchedDate}.
          </p>


          <p className="mt-3 flex flex-wrap items-center gap-2 text-ink-muted">
            <SourceBadge kind="offentlig" source={s.source_name} />
            <span>·</span>
            <Link to="/om/metod" className="text-primary underline underline-offset-2">Metod & källor</Link>
            <a href={s.source_url} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2">
              Öppna källan
            </a>
          </p>
          <StaleWarning updatedAt={s.fetched_at} />
          <ShareButtons
            className="mt-4"
            path={`/skola/${s.slug}`}
            title={`${s.name} — skolprofil på Skolvyn`}
            text={`Se ${s.name} med officiella uppgifter från ${sourceShortLabel(s.education_types)}.`}
            contentType="school_unit"
            itemId={s.slug}
            label="Dela profilen"
          />
          <span className="ml-4 inline-flex">
            <ReportContentDialog
              schoolUnitCode={s.school_unit_code}
              contentType="profiltext"
              contentId={s.school_unit_code}
              label="Anmäl innehåll"
              what={`Texter som ${s.name} själva publicerat`}
            />
          </span>
        </section>
      </div>

      {/* Effekt 13 — sticky åtgärdsrad. Glider in efter 400 px och göms när
          intresseanmälan syns, så att den aldrig konkurrerar med den riktiga
          ytan. Sidan reserverar plats i botten (pb nedan) på mobil. */}
      <StickyActionBar hideWhenVisibleId="intresseanmalan">
        <Link
          to="/skola/$slug/omdome"
          params={{ slug: s.slug }}
          search={{ inbjudan: undefined, utkast: undefined }}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-card border border-border bg-background px-3 text-small font-semibold text-ink active:scale-[0.98]"
          aria-label="Skriv omdöme"
        >
          <PenSquare className="h-4 w-4" />
          <span>Omdöme</span>
        </Link>
        <Link
          to="/skola/$slug/intresseanmalan"
          params={{ slug: s.slug }}
          className="inline-flex h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-card bg-primary px-3 text-small font-semibold text-primary-foreground shadow-1 active:scale-[0.98]"
        >
          <Mail className="h-4 w-4 shrink-0" /> <span className="truncate">Intresseanmälan</span>
        </Link>
      </StickyActionBar>

    </div>
  );
}





function SectionHeader({
  id,
  eyebrow,
  title,
  description,
  action,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div id={id} className="mt-10 scroll-mt-24 grid gap-2 border-t border-border pt-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
      <div className="min-w-0">
        <p className="eyebrow text-primary">{eyebrow}</p>
        <h2 className="mt-1 font-display text-h3 md:text-h2">{title}</h2>
        {description && <p className="mt-1.5 max-w-2xl text-small text-ink-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}




function SchoolReviews({ slug, schoolName }: { slug: string; schoolName: string }) {
  const { data: reviews, isLoading: reviewsLoading } = useQuery({
    queryKey: ["reviews", "school", slug],
    queryFn: () => listApprovedReviews({ data: { schoolSlug: slug, limit: 6 } }),
    staleTime: 60_000,
  });
  const { data: stats } = useQuery({
    queryKey: ["reviews", "stats", slug],
    queryFn: () => getReviewStats({ data: { schoolSlug: slug } }),
    staleTime: 60_000,
  });

  const items = reviews ?? [];
  const count = stats?.count ?? 0;
  const average = stats?.average ?? 0;
  const maxBar = Math.max(1, ...(stats?.distribution.map((d) => d.count) ?? [1]));

  return (
    <section aria-labelledby="omdomen" className="mt-10 scroll-mt-24 border-t border-border pt-6">
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div>
          <p className="eyebrow text-primary">Omdömen</p>
          <h2 id="omdomen" className="mt-1 font-display text-h3 md:text-h2">
            Vad familjer och elever <span className="underline-accent">säger</span>
          </h2>
        </div>
        <Link
          to="/skola/$slug/omdome"
          params={{ slug }}
          search={{ inbjudan: undefined, utkast: undefined }}
          className="inline-flex items-center gap-2 rounded-card bg-primary px-4 py-2.5 text-small font-semibold text-primary-foreground hover:bg-primary-hover"
        >
          <PenSquare className="h-4 w-4" /> Skriv omdöme
        </Link>
      </div>

      {reviewsLoading ? (
        <p className="mt-6 text-small text-ink-muted">Hämtar omdömen…</p>
      ) : count === 0 ? (
        <p className="mt-4 flex flex-wrap items-center gap-2 rounded-card border border-dashed border-border bg-surface px-4 py-3 text-small text-ink-muted">
          <MessageSquare className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          Har du erfarenhet av {schoolName}?
          <Link
            to="/skola/$slug/omdome"
            params={{ slug }}
            search={{ inbjudan: undefined, utkast: undefined }}
            className="font-semibold text-primary underline underline-offset-2"
          >
            Skriv ett omdöme
          </Link>
        </p>
      ) : (
        <>
          <div className="mt-6 grid gap-4 rounded-card border border-border bg-surface p-card md:grid-cols-[minmax(0,220px)_minmax(0,1fr)] md:items-center">
            <div className="flex flex-col items-center gap-1 border-b border-border pb-4 md:border-b-0 md:border-r md:pb-0 md:pr-6">
              <p className="font-display text-display leading-none text-ink">{average.toFixed(1)}</p>
              <StarRating value={average} size={18} />
              <p className="mono text-small text-ink-muted">
                {count} omdöme{count === 1 ? "" : "n"}
              </p>
            </div>
            <ul className="space-y-1.5">
              {(stats?.distribution ?? []).map((d) => (
                <li key={d.rating} className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  <span className="mono inline-flex items-center gap-1 text-small text-ink-muted">
                    {d.rating} <Star className="h-3 w-3 fill-primary text-primary" />
                  </span>
                  <span className="h-2 overflow-hidden rounded-pill bg-background">
                    <span
                      className="block h-full rounded-pill bg-primary"
                      style={{ width: `${(d.count / maxBar) * 100}%` }}
                    />
                  </span>
                  <span className="mono w-8 text-right text-small text-ink-muted">{d.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {items.slice(0, 4).map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>

          {count > 4 && (
            <div className="mt-6 text-center">
              <Link
                to="/omdomen"
                className="inline-flex items-center gap-1.5 rounded-card border border-border bg-surface px-4 py-2.5 text-small font-semibold text-ink transition hover:border-primary hover:text-primary"
              >
                Visa alla {count} omdömen <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function Card({
  icon,
  title,
  source,
  children,
  updatedAt,
  sourceName,
  sourceUrl,
  sourceLabel,
}: {
  icon: React.ReactNode;
  title: string;
  source?: React.ReactNode;
  children: React.ReactNode;
  /** ISO-datum för när uppgifterna senast uppdaterades. */
  updatedAt?: string | null;
  /** Källans namn, t.ex. "Skolverket" eller "Skolan själv". */
  sourceName?: string;
  /** Extern länk till källan (öppnas i ny flik). */
  sourceUrl?: string | null;
  /** Etikett för källan i footern — default "Källa". */
  sourceLabel?: string;
}) {
  return (
    <section className="rounded-card border border-border bg-surface p-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-small font-semibold text-ink">
          <span className="grid h-7 w-7 place-items-center rounded-field bg-primary-soft text-primary">{icon}</span>
          {title}
        </h3>
        {source}
      </div>
      <dl className="mt-4 space-y-3 text-small">{children}</dl>
      {(updatedAt || sourceName) && (
        <CardMeta
          updatedAt={updatedAt}
          sourceName={sourceName}
          sourceUrl={sourceUrl ?? undefined}
          sourceLabel={sourceLabel}
        />
      )}
    </section>
  );
}

function CardMeta({
  updatedAt,
  sourceName,
  sourceUrl,
  sourceLabel = "Källa",
}: {
  updatedAt?: string | null;
  sourceName?: string;
  sourceUrl?: string;
  sourceLabel?: string;
}) {
  const formatted = updatedAt ? formatSwedishDate(updatedAt) : null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/60 pt-3 text-small text-ink-muted">
      {formatted && (
        <span>
          <span className="uppercase tracking-wide">Senast uppdaterad</span>{" "}
          <time dateTime={updatedAt ?? undefined} className="font-medium text-ink">
            {formatted}
          </time>
        </span>
      )}
      {sourceName && (
        <span>
          <span className="uppercase tracking-wide">{sourceLabel}</span>{" "}
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-primary underline underline-offset-2"
            >
              {sourceName}
            </a>
          ) : (
            <span className="font-medium text-ink">{sourceName}</span>
          )}
        </span>
      )}
      <Link to="/om/metod" className="ml-auto text-primary underline underline-offset-2">
        Metod
      </Link>
    </div>
  );
}

function formatSwedishDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function Row({
  label,
  value,
  mono,
  icon,
  verified,
  verifiedSource,
  note,
}: {
  label: string;
  value: React.ReactNode | null;
  mono?: boolean;
  icon?: React.ReactNode;
  /** Liten rad under värdet, t.ex. varifrån uppgiften kommer. */
  note?: React.ReactNode;
  /** Visa en "Verifierad"-badge intill värdet (endast när värdet finns). */
  verified?: boolean;
  /** Källa som visas i badgens tooltip, t.ex. "Skolverket". */
  verifiedSource?: string;
}) {
  const hasValue = value !== null && value !== undefined && value !== "";
  // Saknas uppgiften visas ingenting — ingen rad som beskriver tomrummet.
  if (!hasValue) return null;
  return (
    <div className="grid grid-cols-1 items-baseline gap-1 border-b border-border/60 pb-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:gap-3 last:border-b-0 last:pb-0">
      <dt className="text-small uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className={`flex flex-wrap items-center gap-1.5 text-ink ${mono ? "mono" : ""}`}>
        {icon}
        <span>{value}</span>
        {hasValue && verified && <VerifiedBadge size="sm" source={verifiedSource} />}
        {hasValue && note && <div className="w-full">{note}</div>}
      </dd>
    </div>
  );
}

/**
 * Källrad per kontaktuppgift. Skolans uppgift ersätter aldrig registrets i
 * databasen — men föräldern ska bara se ett nummer att ringa. Registrets
 * avvikande värde redovisas för granskaren i admin, inte här.
 */
function ContactSource({ fromSchool, sourceName }: { fromSchool: boolean; sourceName: string }) {
  return (
    <span className="text-small text-ink-muted">
      {fromSchool ? "Uppgift från skolan" : `Uppgift från ${sourceName}`}
    </span>
  );
}


function ensureUrl(v: string) {
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}
